type ExerciseTemplate = {
  name: string
  primary_movers: string
  secondary_movers: string[]
  bodyweight: boolean
  assisted: boolean
}

/**
 * Normalize exercise name for fuzzy matching
 */
function normalizeExerciseName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^\w]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
}

/**
 * Create reverse mapping from normalized exercise names to template IDs
 */
function createNameToTemplateMapping(
  templates: Record<string, ExerciseTemplate>
): Record<string, string> {
  const nameMapping: Record<string, string> = {}

  Object.entries(templates).forEach(([templateId, template]) => {
    const normalizedName = normalizeExerciseName(template.name)
    nameMapping[normalizedName] = templateId
  })

  return nameMapping
}

export const exerciseTemplates: Record<string, ExerciseTemplate> = {
  '12017185': {
    name: 'Chest Fly (Dumbbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '29083183': {
    name: 'Chin Up',
    primary_movers: 'lats',
    secondary_movers: ['biceps', 'upperBack'],
    bodyweight: true,
    assisted: false
  },
  '67280085': {
    name: 'Pullover (Dumbbell)',
    primary_movers: 'lats',
    secondary_movers: ['chest', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '78683336': {
    name: 'Chest Fly (Machine)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '84325755': {
    name: 'Skiing',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '89304423': {
    name: 'Kettlebell Snatch',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'upperBack', 'glutes', 'hamstrings', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '99507114': {
    name: 'Deadlift (Band)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'lats', 'upperBack', 'quads'],
    bodyweight: false,
    assisted: false
  },
  '91237BDD': {
    name: 'Calf Press (Machine)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  D928C232: {
    name: 'Crunch (Weighted)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '37FCC2BB': {
    name: 'Bicep Curl (Dumbbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '7E3BC8B6': {
    name: 'Hammer Curl (Dumbbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '8347DFD1': {
    name: 'Single Arm Tricep Extension (Dumbbell)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '422B08F1': {
    name: 'Lateral Raise (Dumbbell)',
    primary_movers: 'lateralDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '1E42FD5F': {
    name: 'Hack Squat (Machine)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '729237D1': {
    name: 'Pull Up (Weighted)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  A57D38D5: {
    name: 'Floor Triceps Dip',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: true,
    assisted: false
  },
  '3601968B': {
    name: 'Bench Press (Dumbbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '07B38369': {
    name: 'Incline Bench Press (Dumbbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  ABEC557F: {
    name: 'Shrug (Dumbbell)',
    primary_movers: 'upperBack',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '0393F233': {
    name: 'Seated Cable Row - V Grip (Cable)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '4E5257DE': {
    name: 'Lat Pulldown - Close Grip (Cable)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  '1B2B1E7C': {
    name: 'Pull Up',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: true,
    assisted: false
  },
  B123DD01: {
    name: 'Pullover (Machine)',
    primary_movers: 'lats',
    secondary_movers: ['chest'],
    bodyweight: false,
    assisted: false
  },
  B567DD46: {
    name: 'Reverse Curl (Dumbbell)',
    primary_movers: 'forearms',
    secondary_movers: ['biceps'],
    bodyweight: false,
    assisted: false
  },
  '2B4B7310': {
    name: 'Romanian Deadlift (Barbell)',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes', 'lats', 'upperBack'],
    bodyweight: false,
    assisted: false
  },
  C7973E0E: {
    name: 'Leg Press (Machine)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '8BEBFED6': {
    name: 'Hip Adduction (Machine)',
    primary_movers: 'adductors',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '11A123F3': {
    name: 'Seated Leg Curl (Machine)',
    primary_movers: 'hamstrings',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '75A4F6C4': {
    name: 'Leg Extension (Machine)',
    primary_movers: 'quads',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '0FBF7195': {
    name: 'Bench Press (Smith Machine)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '3BC06AD3': {
    name: '21s Bicep Curl',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  B4F2FF72: {
    name: 'Ab Scissors',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '99D5F10E': {
    name: 'Ab Wheel',
    primary_movers: 'abs',
    secondary_movers: ['lats', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '5E0DDACE': {
    name: 'Aerobics',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '43573BB8': {
    name: 'Air Bike',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: false,
    assisted: false
  },
  A69FF221: {
    name: 'Arnold Press (Dumbbell)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '6fb64f52-c9b4-4e6d-87a3-d278f56c0022': {
    name: 'Arnold Press (Dumbell Standing)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  D4A2FE7E: {
    name: 'Around The World',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '5BFF35BA': {
    name: 'Assisted Pistol Squats',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: true
  },
  '4F5866F8': {
    name: 'Back Extension (Hyperextension)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: true,
    assisted: false
  },
  A05C064D: {
    name: 'Back Extension (Machine)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: false,
    assisted: false
  },
  '091737FA': {
    name: 'Back Extension (Weighted Hyperextension)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: false,
    assisted: false
  },
  F51D9080: {
    name: 'Ball Slams',
    primary_movers: 'abs',
    secondary_movers: ['lats', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  E8D86EE8: {
    name: 'Band Pullaparts',
    primary_movers: 'rearDelts',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  '084A67CA': {
    name: 'Battle Ropes',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts'],
    bodyweight: false,
    assisted: false
  },
  DDB29047: {
    name: 'Behind the Back Bicep Wrist Curl (Barbell)',
    primary_movers: 'forearms',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  CD6DC8E5: {
    name: 'Bench Dip',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: true,
    assisted: false
  },
  '35B51B87': {
    name: 'Bench Press - Close Grip (Barbell)',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  E644F828: {
    name: 'Bench Press - Wide Grip (Barbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '79D0BB3A': {
    name: 'Bench Press (Barbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '99C1F2AD': {
    name: 'Bench Press (Cable)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  EA820646: {
    name: 'Bent Over Row (Band)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '55E6546F': {
    name: 'Bent Over Row (Barbell)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '23E92538': {
    name: 'Bent Over Row (Dumbbell)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  A5AC6449: {
    name: 'Bicep Curl (Barbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  ADA8623C: {
    name: 'Bicep Curl (Cable)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  AF328E3D: {
    name: 'Bicep Curl (Machine)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '0B4C1902': {
    name: 'Bicep Curl (Suspension)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: true,
    assisted: false
  },
  A41C7261: {
    name: 'Bicycle Crunch',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '594450D2': {
    name: 'Bicycle Crunch Raised Legs',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  BD0AD077: {
    name: 'Bird Dog',
    primary_movers: 'abs',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  '56092DD1': {
    name: 'Box Jump',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  A0D8FA1B: {
    name: 'Boxing',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: true,
    assisted: false
  },
  '38FC1AB9': {
    name: 'Box Squat (Barbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  B5D3A742: {
    name: 'Bulgarian Split Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  BB792A36: {
    name: 'Burpee',
    primary_movers: 'quads',
    secondary_movers: ['chest', 'frontDelts', 'triceps', 'abs'],
    bodyweight: true,
    assisted: false
  },
  '86B00DDE': {
    name: 'Burpee Over the Bar',
    primary_movers: 'quads',
    secondary_movers: ['chest', 'frontDelts', 'triceps', 'abs'],
    bodyweight: true,
    assisted: false
  },
  '9DCE2D64': {
    name: 'Butterfly (Pec Deck)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  CC55119B: {
    name: 'Cable Core Palloff Press',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '23A48484': {
    name: 'Cable Crunch',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '651F844C': {
    name: 'Cable Fly Crossovers',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '8C331CD8': {
    name: 'Cable Pull Through',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: false,
    assisted: false
  },
  '92D858EA': {
    name: 'Cable Twist (Down to up)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  A2D838BD: {
    name: 'Cable Twist (Up to down)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '47B9DF13': {
    name: 'Calf Extension (Machine)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '6FCD7755': {
    name: 'Chest Dip',
    primary_movers: 'chest',
    secondary_movers: ['triceps', 'frontDelts'],
    bodyweight: true,
    assisted: false
  },
  E9E4089F: {
    name: 'Chest Dip (Assisted)',
    primary_movers: 'chest',
    secondary_movers: ['triceps', 'frontDelts'],
    bodyweight: false,
    assisted: true
  },
  '29472BE1': {
    name: 'Chest Dip (Weighted)',
    primary_movers: 'chest',
    secondary_movers: ['triceps', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  DDB7C19F: {
    name: 'Chest Fly (Band)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '720B0D70': {
    name: 'Chest Fly (Suspension)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: true,
    assisted: false
  },
  EAC7D9C5: {
    name: 'Chest Press (Band)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '7EB3F7C3': {
    name: 'Chest Press (Machine)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '914F3A96': {
    name: 'Chest Supported Incline Row (Dumbbell)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  B582299E: {
    name: 'Chest Supported Reverse Fly (Dumbbell)',
    primary_movers: 'rearDelts',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  F21D5693: {
    name: 'Chest Supported Y Raise (Dumbbell)',
    primary_movers: 'rearDelts',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  D23C609B: {
    name: 'Chin Up (Assisted)',
    primary_movers: 'lats',
    secondary_movers: ['biceps', 'upperBack'],
    bodyweight: false,
    assisted: true
  },
  '023943F1': {
    name: 'Chin Up (Weighted)',
    primary_movers: 'lats',
    secondary_movers: ['biceps', 'upperBack'],
    bodyweight: false,
    assisted: false
  },
  CC016611: {
    name: 'Clamshell',
    primary_movers: 'glutes',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '10D76E8F': {
    name: 'Clap Push Ups',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: true,
    assisted: false
  },
  ABB00838: {
    name: 'Clean',
    primary_movers: 'upperBack',
    secondary_movers: ['glutes', 'hamstrings', 'quads', 'frontDelts', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '9E09CEC3': {
    name: 'Clean and Jerk',
    primary_movers: 'frontDelts',
    secondary_movers: ['upperBack', 'glutes', 'hamstrings', 'quads', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  D3095577: {
    name: 'Clean and Press',
    primary_movers: 'frontDelts',
    secondary_movers: ['upperBack', 'glutes', 'hamstrings', 'quads', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '652FEA39': {
    name: 'Clean Pull',
    primary_movers: 'upperBack',
    secondary_movers: ['glutes', 'hamstrings', 'quads', 'lats'],
    bodyweight: false,
    assisted: false
  },
  E23F1F2B: {
    name: 'Climbing',
    primary_movers: 'lats',
    secondary_movers: ['biceps', 'upperBack', 'forearms', 'quads', 'glutes'],
    bodyweight: true,
    assisted: false
  },
  '32C4D4A2': {
    name: 'Cross Body Hammer Curl',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  DCF3B31B: {
    name: 'Crunch',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '724CDE60': {
    name: 'Concentration Curl',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  EB43ADD4: {
    name: 'Crunch (Machine)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  D39EC9EB: {
    name: 'Curtsy Lunge (Dumbbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'adductors'],
    bodyweight: false,
    assisted: false
  },
  D8F7F851: {
    name: 'Cycling',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: false,
    assisted: false
  },
  D8911FC4: {
    name: 'Dead Bug',
    primary_movers: 'abs',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  B9380898: {
    name: 'Dead Hang',
    primary_movers: 'forearms',
    secondary_movers: ['lats', 'upperBack'],
    bodyweight: true,
    assisted: false
  },
  C6272009: {
    name: 'Deadlift (Barbell)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'lats', 'upperBack', 'quads'],
    bodyweight: false,
    assisted: false
  },
  '5F4E6DD3': {
    name: 'Deadlift (Dumbbell)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'lats', 'upperBack', 'quads'],
    bodyweight: false,
    assisted: false
  },
  '20870ED5': {
    name: 'Deadlift (Smith Machine)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'lats', 'upperBack', 'quads'],
    bodyweight: false,
    assisted: false
  },
  B923B230: {
    name: 'Deadlift (Trap bar)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'lats', 'upperBack', 'quads'],
    bodyweight: false,
    assisted: false
  },
  FA5D0DE1: {
    name: 'Deadlift High Pull',
    primary_movers: 'upperBack',
    secondary_movers: ['glutes', 'hamstrings', 'lats', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  DA0F0470: {
    name: 'Decline Bench Press (Barbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '18487FA7': {
    name: 'Decline Bench Press (Dumbbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  FAF31231: {
    name: 'Decline Bench Press (Machine)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  FFC106CB: {
    name: 'Decline Bench Press (Smith Machine)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  A351AED7: {
    name: 'Decline Chest Fly (Dumbbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  BC10A922: {
    name: 'Decline Crunch',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  B2398CD1: {
    name: 'Decline Crunch (Weighted)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  C43825EA: {
    name: 'Decline Push Up',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: true,
    assisted: false
  },
  '6575F52D': {
    name: 'Diamond Push Up',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: true,
    assisted: false
  },
  '7DA843A3': {
    name: 'Downward Dog',
    primary_movers: 'frontDelts',
    secondary_movers: ['hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  D950429E: {
    name: 'Drag Curl',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  AFC29472: {
    name: 'Dragon Flag',
    primary_movers: 'abs',
    secondary_movers: ['lats'],
    bodyweight: true,
    assisted: false
  },
  B7192800: {
    name: 'Dragonfly',
    primary_movers: 'abs',
    secondary_movers: ['lats'],
    bodyweight: true,
    assisted: false
  },
  F1E57334: {
    name: 'Dumbbell Row',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  F3717B0E: {
    name: 'Dumbbell Snatch',
    primary_movers: 'frontDelts',
    secondary_movers: ['upperBack', 'glutes', 'hamstrings', 'quads'],
    bodyweight: false,
    assisted: false
  },
  F72FA239: {
    name: 'Dumbbell Squeeze Press',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  BF6ECE89: {
    name: 'Dumbbell Step Up',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: false,
    assisted: false
  },
  A01BE509: {
    name: 'Elbow to Knee',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '3303376C': {
    name: 'Elliptical Trainer',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '01A35BF9': {
    name: 'EZ Bar Biceps Curl',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  BE640BA0: {
    name: 'Face Pull',
    primary_movers: 'rearDelts',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  '50C613D0': {
    name: 'Farmers Walk',
    primary_movers: 'forearms',
    secondary_movers: ['upperBack', 'quads', 'glutes'],
    bodyweight: false,
    assisted: false
  },
  '2AF8B552': {
    name: 'Fire Hydrants',
    primary_movers: 'glutes',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  D53F2886: {
    name: 'Floor Press (Barbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '756EE329': {
    name: 'Floor Press (Dumbbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '2FA77B28': {
    name: 'Flutter Kicks',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '207DC636': {
    name: 'Frog Jumps',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  F6948F17: {
    name: 'Frog Pumps (Dumbbell)',
    primary_movers: 'glutes',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '0E608350': {
    name: 'Front Lever Hold',
    primary_movers: 'lats',
    secondary_movers: ['abs', 'upperBack'],
    bodyweight: true,
    assisted: false
  },
  '30F03BF0': {
    name: 'Front Lever Raise',
    primary_movers: 'lats',
    secondary_movers: ['abs', 'upperBack'],
    bodyweight: true,
    assisted: false
  },
  '47B036EF': {
    name: 'Front Raise (Band)',
    primary_movers: 'frontDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  BD86EFD5: {
    name: 'Front Raise (Barbell)',
    primary_movers: 'frontDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  DBB91A3C: {
    name: 'Front Raise (Cable)',
    primary_movers: 'frontDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '796E8E2C': {
    name: 'Front Raise (Suspension)',
    primary_movers: 'frontDelts',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '8293E554': {
    name: 'Front Raise (Dumbbell)',
    primary_movers: 'frontDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '5046D0A9': {
    name: 'Front Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '1283BBA6': {
    name: 'Full Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  CDA23948: {
    name: 'Glute Bridge',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: true,
    assisted: false
  },
  '68B83EE0': {
    name: 'Glute Ham Raise',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  CBA02382: {
    name: 'Glute Kickback (Machine)',
    primary_movers: 'glutes',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '987234AB': {
    name: 'Glute Kickback on Floor',
    primary_movers: 'glutes',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '3D0C7C75': {
    name: 'Goblet Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '4180C405': {
    name: 'Good Morning (Barbell)',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '05487216': {
    name: 'Gorilla Row (Kettlebell)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  AC5A4C58: {
    name: 'Hack Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '1D4B3D6B': {
    name: 'Hammer Curl (Band)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '36E8F14E': {
    name: 'Hammer Curl (Cable)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  BE3615CF: {
    name: 'Handstand Hold',
    primary_movers: 'frontDelts',
    secondary_movers: ['triceps'],
    bodyweight: true,
    assisted: false
  },
  '90B04F96': {
    name: 'Handstand Push Up',
    primary_movers: 'frontDelts',
    secondary_movers: ['triceps'],
    bodyweight: true,
    assisted: false
  },
  BD4E7E53: {
    name: 'Hang Clean',
    primary_movers: 'upperBack',
    secondary_movers: ['glutes', 'hamstrings', 'quads', 'frontDelts', 'calves'],
    bodyweight: false,
    assisted: false
  },
  F4E77594: {
    name: 'Hang Snatch',
    primary_movers: 'frontDelts',
    secondary_movers: ['upperBack', 'glutes', 'hamstrings', 'quads', 'calves'],
    bodyweight: false,
    assisted: false
  },
  F8356514: {
    name: 'Hanging Leg Raise',
    primary_movers: 'abs',
    secondary_movers: ['forearms'],
    bodyweight: true,
    assisted: false
  },
  '08590920': {
    name: 'Hanging Knee Raise',
    primary_movers: 'abs',
    secondary_movers: ['forearms'],
    bodyweight: true,
    assisted: false
  },
  '567FB505': {
    name: 'Heel Taps',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  BE89C631: {
    name: 'Hex Press (Dumbbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  C9CCB878: {
    name: 'High Knee Skips',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '150E076B': {
    name: 'High Knees',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '023947AB': {
    name: 'HIIT',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '1C34A172': {
    name: 'Hiking',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  F4B4C6EE: {
    name: 'Hip Abduction (Machine)',
    primary_movers: 'glutes',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '92B8C7E1': {
    name: 'Hip Thrust',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: true,
    assisted: false
  },
  D57C2EC7: {
    name: 'Hip Thrust (Barbell)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: false,
    assisted: false
  },
  '68CE0B9B': {
    name: 'Hip Thrust (Machine)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: false,
    assisted: false
  },
  '970ADF87': {
    name: 'Hollow Rock',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '50DFDFAB': {
    name: 'Incline Bench Press (Barbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '3A6FA3D1': {
    name: 'Incline Bench Press (Smith Machine)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  D3E2AB55: {
    name: 'Incline Chest Fly (Dumbbell)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  FBF92739: {
    name: 'Incline Chest Press (Machine)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '39C99849': {
    name: 'Incline Push Ups',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: true,
    assisted: false
  },
  '425805F4': {
    name: 'Inverted Row',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: true,
    assisted: false
  },
  '24706DCD': {
    name: 'Iso-Lateral Chest Press (Machine)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  BC3492DA: {
    name: 'Iso-Lateral High Row (Machine)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '91FAFBA3': {
    name: 'Iso-Lateral Low Row',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  AA1EB7D8: {
    name: 'Iso-Lateral Row (Machine)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  D410F649: {
    name: 'Jack Knife (Suspension)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '32B0B590': {
    name: 'Jackknife Sit Up',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '74103F24': {
    name: 'JM Press (Barbell)',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '040BA2E3': {
    name: 'Jump Rope',
    primary_movers: 'calves',
    secondary_movers: ['quads', 'glutes'],
    bodyweight: true,
    assisted: false
  },
  BA3DE542: {
    name: 'Jump Shrug',
    primary_movers: 'upperBack',
    secondary_movers: ['quads', 'glutes', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '70D4EBBF': {
    name: 'Jump Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '991833C2': {
    name: 'Jumping Jack',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves', 'lateralDelts'],
    bodyweight: true,
    assisted: false
  },
  '5CC07A1F': {
    name: 'Jumping Lunge',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  F99C211D: {
    name: 'Kettlebell Clean',
    primary_movers: 'upperBack',
    secondary_movers: ['glutes', 'hamstrings', 'quads', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '4E239ED8': {
    name: 'Kettlebell Curl',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  A127DA73: {
    name: 'Kettlebell Goblet Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '9B13BA4B': {
    name: 'Kettlebell High Pull',
    primary_movers: 'upperBack',
    secondary_movers: ['glutes', 'hamstrings', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '6433CD93': {
    name: 'Kettlebell Shoulder Press',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  F8A0FCCA: {
    name: 'Kettlebell Swing',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '5F8903BF': {
    name: 'Kettlebell Turkish Get Up',
    primary_movers: 'abs',
    secondary_movers: ['frontDelts', 'glutes', 'quads'],
    bodyweight: false,
    assisted: false
  },
  A91838C0: {
    name: 'Kipping Pull Up',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'abs'],
    bodyweight: true,
    assisted: false
  },
  '98237BA2': {
    name: 'Knee Raise Parallel Bars',
    primary_movers: 'abs',
    secondary_movers: ['triceps'],
    bodyweight: true,
    assisted: false
  },
  D82EA543: {
    name: 'Kneeling Pulldown (band)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  B74A95BB: {
    name: 'Kneeling Push Up',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: true,
    assisted: false
  },
  '6D54A050': {
    name: 'L-Sit Hold',
    primary_movers: 'abs',
    secondary_movers: ['triceps', 'frontDelts'],
    bodyweight: true,
    assisted: false
  },
  '923874CA': {
    name: 'Landmine 180',
    primary_movers: 'abs',
    secondary_movers: ['frontDelts', 'lateralDelts'],
    bodyweight: false,
    assisted: false
  },
  D7D7FCCE: {
    name: 'Landmine Row',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '983274DD': {
    name: 'Landmine Squat and Press',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  D2FE7B2E: {
    name: 'Lat Pulldown (Band)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  '6A6C31A5': {
    name: 'Lat Pulldown (Cable)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  '473CF5B8': {
    name: 'Lat Pulldown (Machine)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  EC02979E: {
    name: 'Lateral Band Walks',
    primary_movers: 'glutes',
    secondary_movers: ['quads'],
    bodyweight: false,
    assisted: false
  },
  '07EBC4DC': {
    name: 'Lateral Box Jump',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  DC59D143: {
    name: 'Lateral Leg Raises',
    primary_movers: 'glutes',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  F2A37357: {
    name: 'Lateral Lunge',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'adductors'],
    bodyweight: true,
    assisted: false
  },
  DF200976: {
    name: 'Lateral Raise (Band)',
    primary_movers: 'lateralDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  BE289E45: {
    name: 'Lateral Raise (Cable)',
    primary_movers: 'lateralDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  D5D0354D: {
    name: 'Lateral Raise (Machine)',
    primary_movers: 'lateralDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  D731CCA8: {
    name: 'Lateral Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'adductors'],
    bodyweight: true,
    assisted: false
  },
  '293483AD': {
    name: 'Low Cable Fly Crossovers',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '0482DA98': {
    name: 'Leg Raise Parallel Bars',
    primary_movers: 'abs',
    secondary_movers: ['triceps'],
    bodyweight: true,
    assisted: false
  },
  '0EB695C9': {
    name: 'Leg Press Horizontal (Machine)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '8A2E6481': {
    name: 'Low Row (Suspension)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: true,
    assisted: false
  },
  '5E1A7777': {
    name: 'Lunge',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  '6E6EE645': {
    name: 'Lunge (Barbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  B537D09F: {
    name: 'Lunge (Dumbbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  BD5935CF: {
    name: 'Lying Knee Raise',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  B8127AD1: {
    name: 'Lying Leg Curl (Machine)',
    primary_movers: 'hamstrings',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '09C9F635': {
    name: 'Lying Leg Raise',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '22FEDCA4': {
    name: 'Lying Neck Curls',
    primary_movers: 'neck',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '31B8DA0F': {
    name: 'Lying Neck Curls (Weighted)',
    primary_movers: 'neck',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '0910BD28': {
    name: 'Lying Neck Extension',
    primary_movers: 'neck',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  D6A73CF2: {
    name: 'Lying Neck Extension (Weighted)',
    primary_movers: 'neck',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  C732C341: {
    name: 'Meadows Rows (Barbell)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  F49E31D6: {
    name: 'Mountain Climber',
    primary_movers: 'abs',
    secondary_movers: ['frontDelts', 'quads'],
    bodyweight: true,
    assisted: false
  },
  '9F9C164B': {
    name: 'Muscle Up',
    primary_movers: 'lats',
    secondary_movers: ['chest', 'triceps', 'frontDelts', 'upperBack'],
    bodyweight: true,
    assisted: false
  },
  F60BDDF8: {
    name: 'Negative Pull Up',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: true,
    assisted: false
  },
  '108D7A14': {
    name: 'Nordic Hamstrings Curls',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  DBE341AA: {
    name: 'Oblique Crunch',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  B140095E: {
    name: 'One Arm Push Up',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps', 'abs'],
    bodyweight: true,
    assisted: false
  },
  '582ADA23': {
    name: 'Overhead Curl (Cable)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '4C6721B9': {
    name: 'Overhead Dumbbell Lunge',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '7B8D84E8': {
    name: 'Overhead Press (Barbell)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '6AC96645': {
    name: 'Overhead Press (Dumbbell)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  B09A1304: {
    name: 'Overhead Press (Smith Machine)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '2CFED196': {
    name: 'Overhead Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  CE1054CE: {
    name: 'Pause Squat (Barbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '018ADC12': {
    name: 'Pendlay Row (Barbell)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '30E293E3': {
    name: 'Pendulum Squat (Machine)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '0EFE8162': {
    name: 'Pike Pushup',
    primary_movers: 'frontDelts',
    secondary_movers: ['triceps', 'abs'],
    bodyweight: true,
    assisted: false
  },
  EC2510CD: {
    name: 'Pilates',
    primary_movers: 'abs',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  '942BAD12': {
    name: 'Pinwheel Curl (Dumbbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '3FF6A22E': {
    name: 'Pistol Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  C6C9B8A0: {
    name: 'Plank',
    primary_movers: 'abs',
    secondary_movers: ['frontDelts', 'glutes'],
    bodyweight: true,
    assisted: false
  },
  '31436F5D': {
    name: 'Plank Pushup',
    primary_movers: 'abs',
    secondary_movers: ['chest', 'frontDelts', 'triceps'],
    bodyweight: true,
    assisted: false
  },
  '9283ABD5': {
    name: 'Plate Curl',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  DBF9273A: {
    name: 'Plate Front Raise',
    primary_movers: 'frontDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '982734D4': {
    name: 'Plate Press',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '9373FSD1': {
    name: 'Plate Squeeze (Svend Press)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  C628D768: {
    name: 'Power Clean',
    primary_movers: 'upperBack',
    secondary_movers: ['glutes', 'hamstrings', 'quads', 'frontDelts', 'calves'],
    bodyweight: false,
    assisted: false
  },
  E22F9358: {
    name: 'Power Snatch',
    primary_movers: 'frontDelts',
    secondary_movers: ['upperBack', 'glutes', 'hamstrings', 'quads', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '4F942934': {
    name: 'Preacher Curl (Barbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  FAB6EB2F: {
    name: 'Preacher Curl (Dumbbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '1E9A6B8E': {
    name: 'Preacher Curl (Machine)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '84A77566': {
    name: 'Press Under',
    primary_movers: 'frontDelts',
    secondary_movers: ['triceps', 'quads'],
    bodyweight: false,
    assisted: false
  },
  '2C37EC5E': {
    name: 'Pull Up (Assisted)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: true
  },
  '56808FD2': {
    name: 'Pull Up (Band)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: true,
    assisted: false
  },
  '542F3CD5': {
    name: 'Push Press',
    primary_movers: 'frontDelts',
    secondary_movers: ['quads', 'glutes', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '392887AA': {
    name: 'Push Up',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: true,
    assisted: false
  },
  '947DAC23': {
    name: 'Push Up - Close Grip',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: true,
    assisted: false
  },
  '19372ABC': {
    name: 'Push Up (Weighted)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  FE389074: {
    name: 'Rack Pull',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'lats', 'upperBack'],
    bodyweight: false,
    assisted: false
  },
  C315DC2A: {
    name: 'Rear Delt Reverse Fly (Cable)',
    primary_movers: 'rearDelts',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  E5988A0A: {
    name: 'Rear Delt Reverse Fly (Dumbbell)',
    primary_movers: 'rearDelts',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  D8281C62: {
    name: 'Rear Delt Reverse Fly (Machine)',
    primary_movers: 'rearDelts',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  '1B89CA1B': {
    name: 'Renegade Row (Dumbbell)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'abs', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '112FC6B7': {
    name: 'Reverse Curl (Barbell)',
    primary_movers: 'forearms',
    secondary_movers: ['biceps'],
    bodyweight: false,
    assisted: false
  },
  '7952B5CD': {
    name: 'Reverse Crunch',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '9F48F858': {
    name: 'Reverse Curl (Cable)',
    primary_movers: 'forearms',
    secondary_movers: ['biceps'],
    bodyweight: false,
    assisted: false
  },
  '9264ADA1': {
    name: 'Reverse Fly Single Arm (Cable)',
    primary_movers: 'rearDelts',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  '1FF4097B': {
    name: 'Reverse Grip Concentration Curl',
    primary_movers: 'forearms',
    secondary_movers: ['biceps'],
    bodyweight: false,
    assisted: false
  },
  '046E25A2': {
    name: 'Reverse Grip Lat Pulldown (Cable)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  '7C0028C3': {
    name: 'Reverse Hyperextension',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: true,
    assisted: false
  },
  C284D923: {
    name: 'Reverse Lunge',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  '818BA121': {
    name: 'Reverse Lunge (Barbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  FFDA283B: {
    name: 'Reverse Lunge (Dumbbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '99B49684': {
    name: 'Reverse Plank',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'rearDelts'],
    bodyweight: true,
    assisted: false
  },
  '51A0EDAA': {
    name: 'Ring Dips',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: true,
    assisted: false
  },
  '72CFFAD5': {
    name: 'Romanian Deadlift (Dumbbell)',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes', 'lats'],
    bodyweight: false,
    assisted: false
  },
  '234897AB': {
    name: 'Rope Cable Curl',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '9273BA17': {
    name: 'Rope Straight Arm Pulldown',
    primary_movers: 'lats',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  '0222DB42': {
    name: 'Rowing Machine',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'quads', 'glutes'],
    bodyweight: false,
    assisted: false
  },
  AC1BB830: {
    name: 'Running',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  BB83BDDE: {
    name: 'Russian Twist (Bodyweight)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '2982AA23': {
    name: 'Russian Twist (Weighted)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  F1D60854: {
    name: 'Seated Cable Row - Bar Grip',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  C7AE420A: {
    name: 'Scapular Pull Ups',
    primary_movers: 'upperBack',
    secondary_movers: ['lats'],
    bodyweight: true,
    assisted: false
  },
  C3BCABB3: {
    name: 'Seated Cable Row - Bar Wide Grip',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '062AB91A': {
    name: 'Seated Calf Raise',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '6B4C797E': {
    name: 'Seated Chest Flys (Cable)',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '5122E7D9': {
    name: 'Seated Dip Machine',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '8BAB2735': {
    name: 'Seated Incline Curl (Dumbbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '9372FFAA': {
    name: 'Seated Lateral Raise (Dumbbell)',
    primary_movers: 'lateralDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '91AF29E0': {
    name: 'Seated Overhead Press (Barbell)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '9930DF71': {
    name: 'Seated Overhead Press (Dumbbell)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '1006DF48': {
    name: 'Seated Palms Up Wrist Curl',
    primary_movers: 'forearms',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '1DF4A847': {
    name: 'Seated Row (Machine)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '9237BAD1': {
    name: 'Seated Shoulder Press (Machine)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '234BC743': {
    name: 'Seated Triceps Press',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '9202CC23': {
    name: 'Seated Wrist Extension (Barbell)',
    primary_movers: 'forearms',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '878CD1D0': {
    name: 'Shoulder Press (Dumbbell)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '059E835D': {
    name: 'Shoulder Press (Machine Plates)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '502FCE31': {
    name: 'Shoulder Taps',
    primary_movers: 'abs',
    secondary_movers: ['frontDelts'],
    bodyweight: true,
    assisted: false
  },
  '0B841777': {
    name: 'Shrug (Barbell)',
    primary_movers: 'upperBack',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  FFABC123: {
    name: 'Shrug (Cable)',
    primary_movers: 'upperBack',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '19A38071': {
    name: 'Shrug (Machine)',
    primary_movers: 'upperBack',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '742E5BD5': {
    name: 'Shrug (Smith Machine)',
    primary_movers: 'upperBack',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '37F9EDA7': {
    name: 'Side Bend',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '026FD047': {
    name: 'Side Bend (Dumbbell)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  E3EDA509: {
    name: 'Side Plank',
    primary_movers: 'abs',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  '8372BBCC': {
    name: 'Single Arm Cable Crossover',
    primary_movers: 'chest',
    secondary_movers: ['frontDelts'],
    bodyweight: false,
    assisted: false
  },
  D0C4A899: {
    name: 'Single Arm Cable Row',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  B33B526E: {
    name: 'Single Arm Curl (Cable)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '1837BA23': {
    name: 'Single Arm Landmine Press (Barbell)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '2EE45F81': {
    name: 'Single Arm Lat Pulldown',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  DE68C825: {
    name: 'Single Arm Lateral Raise (Cable)',
    primary_movers: 'lateralDelts',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '552AB030': {
    name: 'Single Arm Triceps Pushdown (Cable)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '629AE73D': {
    name: 'Single Leg Extensions',
    primary_movers: 'quads',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  CBC0D834: {
    name: 'Single Leg Glute Bridge',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: true,
    assisted: false
  },
  '487B3755': {
    name: 'Single Leg Hip Thrust',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: true,
    assisted: false
  },
  D1CD146F: {
    name: 'Single Leg Hip Thrust (Dumbbell)',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings'],
    bodyweight: false,
    assisted: false
  },
  '3FD83744': {
    name: 'Single Leg Press (Machine)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '93472AC1': {
    name: 'Single Leg Romanian Deadlift (Barbell)',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '937292AB': {
    name: 'Single Leg Romanian Deadlift (Dumbbell)',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '238BA231': {
    name: 'Single Leg Standing Calf Raise',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  E51CCBE5: {
    name: 'Single Leg Standing Calf Raise (Barbell)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '5DA40761': {
    name: 'Single Leg Standing Calf Raise (Dumbbell)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  B05C2C29: {
    name: 'Single Leg Standing Calf Raise (Machine)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  F5DEF1EB: {
    name: 'Sissy Squat (Weighted)',
    primary_movers: 'quads',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '9237BA12': {
    name: 'Sit Up (Weighted)',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '022DF610': {
    name: 'Sit Up',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '24A809EF': {
    name: 'Skating',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '875F585F': {
    name: 'Skullcrusher (Barbell)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '68F8A292': {
    name: 'Skullcrusher (Dumbbell)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '7757171F': {
    name: 'Sled Push',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: false,
    assisted: false
  },
  FB09C938: {
    name: 'Snatch',
    primary_movers: 'frontDelts',
    secondary_movers: ['upperBack', 'glutes', 'hamstrings', 'quads', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '911A58D3': {
    name: 'Snowboarding',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '2348AB72': {
    name: 'Spider Curl (Barbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '90427D4A': {
    name: 'Spider Curl (Dumbbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  C10A5AC9: {
    name: 'Spiderman',
    primary_movers: 'abs',
    secondary_movers: ['frontDelts'],
    bodyweight: true,
    assisted: false
  },
  E764B907: {
    name: 'Split Jerk',
    primary_movers: 'frontDelts',
    secondary_movers: ['triceps', 'quads'],
    bodyweight: false,
    assisted: false
  },
  '213AB238': {
    name: 'Spinning',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '20C1A3CB': {
    name: 'Split Squat (Dumbbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '9283BABA': {
    name: 'Sprints',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  F1258206: {
    name: 'Squat (Band)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  D04AC939: {
    name: 'Squat (Barbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '9694DA61': {
    name: 'Squat (Bodyweight)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  DCFF3E9F: {
    name: 'Squat (Dumbbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  CC35A01F: {
    name: 'Squat (Machine)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  DDCC3821: {
    name: 'Squat (Smith Machine)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  B82D6418: {
    name: 'Squat (Suspension)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  A8814C06: {
    name: 'Squat Row',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'lats', 'upperBack'],
    bodyweight: false,
    assisted: false
  },
  ACB2751D: {
    name: 'Standing Cable Glute Kickbacks',
    primary_movers: 'glutes',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '06745E58': {
    name: 'Standing Calf Raise',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  E53CCBE5: {
    name: 'Standing Calf Raise (Barbell)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '6DA40660': {
    name: 'Standing Calf Raise (Dumbbell)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  E05C2C38: {
    name: 'Standing Calf Raise (Machine)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  AA52E8D2: {
    name: 'Standing Calf Raise (Smith)',
    primary_movers: 'calves',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '6120CAAB': {
    name: 'Standing Leg Curls',
    primary_movers: 'hamstrings',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '073032BB': {
    name: 'Standing Military Press (Barbell)',
    primary_movers: 'frontDelts',
    secondary_movers: ['lateralDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '128A2381': {
    name: 'Step Up',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  EE2938D1: {
    name: 'Sternum Pull up (Gironda)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: true,
    assisted: false
  },
  D2387AB1: {
    name: 'Straight Arm Lat Pulldown (Cable)',
    primary_movers: 'lats',
    secondary_movers: ['upperBack'],
    bodyweight: false,
    assisted: false
  },
  '2A48E443': {
    name: 'Straight Leg Deadlift',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes', 'lats'],
    bodyweight: false,
    assisted: false
  },
  '527DA061': {
    name: 'Stretching',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes', 'calves'],
    bodyweight: true,
    assisted: false
  },
  D20D7BBE: {
    name: 'Sumo Deadlift',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'quads', 'adductors', 'lats', 'upperBack'],
    bodyweight: false,
    assisted: false
  },
  '3F5F8D40': {
    name: 'Sumo Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'adductors'],
    bodyweight: true,
    assisted: false
  },
  '6622E5A0': {
    name: 'Sumo Squat (Barbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'adductors'],
    bodyweight: false,
    assisted: false
  },
  '05293BCA': {
    name: 'Sumo Squat (Dumbbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'adductors'],
    bodyweight: false,
    assisted: false
  },
  '5E10D0E6': {
    name: 'Sumo Squat (Kettlebell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'adductors'],
    bodyweight: false,
    assisted: false
  },
  '218DA87C': {
    name: 'Superman',
    primary_movers: 'glutes',
    secondary_movers: ['hamstrings', 'upperBack'],
    bodyweight: true,
    assisted: false
  },
  B60A678F: {
    name: 'Swimming',
    primary_movers: 'lats',
    secondary_movers: ['frontDelts', 'triceps', 'quads'],
    bodyweight: true,
    assisted: false
  },
  '08A2974E': {
    name: 'T Bar Row',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps', 'rearDelts'],
    bodyweight: false,
    assisted: false
  },
  '90E506D5': {
    name: 'Thruster (Barbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '10313AFD': {
    name: 'Thruster (Kettlebell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  '75BAC5C3': {
    name: 'Toe Touch',
    primary_movers: 'abs',
    secondary_movers: ['hamstrings'],
    bodyweight: true,
    assisted: false
  },
  B94E35E1: {
    name: 'Toes to Bar',
    primary_movers: 'abs',
    secondary_movers: ['lats', 'forearms'],
    bodyweight: true,
    assisted: false
  },
  FBB62888: {
    name: 'Torso Rotation',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '243710DE': {
    name: 'Treadmill',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: false,
    assisted: false
  },
  '28BB4A95': {
    name: 'Triceps Dip',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: true,
    assisted: false
  },
  '4B4BF8C2': {
    name: 'Triceps Dip (Assisted)',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: false,
    assisted: true
  },
  '10347BAC': {
    name: 'Triceps Dip (Weighted)',
    primary_movers: 'triceps',
    secondary_movers: ['chest', 'frontDelts'],
    bodyweight: false,
    assisted: false
  },
  '3765684D': {
    name: 'Triceps Extension (Dumbbell)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '21310F5F': {
    name: 'Triceps Extension (Cable)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '2F8D3067': {
    name: 'Triceps Extension (Barbell)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  EC3B69A3: {
    name: 'Triceps Kickback (Cable)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '6182EE83': {
    name: 'Triceps Extension (Suspension)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '3092FADD': {
    name: 'Triceps Extension (Machine)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '6127A3AD': {
    name: 'Triceps Kickback (Dumbbell)',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  CDC472B1: {
    name: 'Triceps Pressdown',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '286C1D0B': {
    name: 'Upright Row (Cable)',
    primary_movers: 'lateralDelts',
    secondary_movers: ['frontDelts', 'upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  '7AB9A362': {
    name: 'Upright Row (Barbell)',
    primary_movers: 'lateralDelts',
    secondary_movers: ['frontDelts', 'upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  '94B7239B': {
    name: 'Triceps Rope Pushdown',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '93A552C6': {
    name: 'Triceps Pushdown',
    primary_movers: 'triceps',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '797F0782': {
    name: 'Upright Row (Dumbbell)',
    primary_movers: 'lateralDelts',
    secondary_movers: ['frontDelts', 'upperBack', 'biceps'],
    bodyweight: false,
    assisted: false
  },
  '6BE68B62': {
    name: 'V Up',
    primary_movers: 'abs',
    secondary_movers: [],
    bodyweight: true,
    assisted: false
  },
  '33EDD7DB': {
    name: 'Walking',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '32HKJ34K': {
    name: 'Walking Lunge',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  A733CC5B: {
    name: 'Walking Lunge (Dumbbell)',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  A1F47ACC: {
    name: 'Wall Ball',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'frontDelts', 'triceps'],
    bodyweight: false,
    assisted: false
  },
  C8706C80: {
    name: 'Wall Sit',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: true,
    assisted: false
  },
  '79EF4E4F': {
    name: 'Warm Up',
    primary_movers: 'quads',
    secondary_movers: ['glutes', 'hamstrings', 'calves'],
    bodyweight: true,
    assisted: false
  },
  '7C50F118': {
    name: 'Wide Pull Up',
    primary_movers: 'lats',
    secondary_movers: ['upperBack', 'biceps'],
    bodyweight: true,
    assisted: false
  },
  '0E4523F4': {
    name: 'Wrist Roller',
    primary_movers: 'forearms',
    secondary_movers: [],
    bodyweight: false,
    assisted: false
  },
  '40C6A9FC': {
    name: 'Zercher Squat',
    primary_movers: 'quads',
    secondary_movers: ['glutes'],
    bodyweight: false,
    assisted: false
  },
  '123EE239': {
    name: 'Zottman Curl (Dumbbell)',
    primary_movers: 'biceps',
    secondary_movers: ['forearms'],
    bodyweight: false,
    assisted: false
  },
  '8C9D2928': {
    name: 'Yoga',
    primary_movers: 'hamstrings',
    secondary_movers: ['glutes', 'calves', 'abs'],
    bodyweight: true,
    assisted: false
  }
} as const

/**
 * Pre-computed name-to-template mapping for fast CSV exercise lookup
 */
export const exerciseNameToTemplateMapping = createNameToTemplateMapping(exerciseTemplates)

/**
 * Lookup exercise template by normalized name (for CSV imports)
 */
export function findExerciseTemplateByName(exerciseName: string): ExerciseTemplate | null {
  const normalizedName = normalizeExerciseName(exerciseName)
  const templateId = exerciseNameToTemplateMapping[normalizedName]
  return templateId ? exerciseTemplates[templateId] : null
}

/**
 * Export normalization function for use in other modules
 */
export { normalizeExerciseName }
